import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SubscriptionStatusDashboard = ({ subscriptions, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const filteredSubscriptions = subscriptions?.filter(sub => {
    const matchesSearch = sub?.user_profiles?.full_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         sub?.user_profiles?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesStatus = filterStatus === 'all' || sub?.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadgeColor = (status) => {
    const colors = {
      active: 'bg-success/10 text-success',
      paused: 'bg-warning/10 text-warning',
      cancelled: 'bg-destructive/10 text-destructive',
      expired: 'bg-muted text-muted-foreground',
      pending: 'bg-primary/10 text-primary'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getBillingCycleLabel = (cycle) => {
    const labels = {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      semi_annual: 'Semi-Annual',
      annual: 'Annual'
    };
    return labels?.[cycle] || cycle;
  };

  return (
    <div className="bg-card rounded-xl shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-heading font-bold text-foreground flex items-center gap-2">
          <Icon name="CreditCard" size={24} color="var(--color-primary)" />
          Subscription Status Dashboard
        </h2>
        <Button onClick={onRefresh} variant="ghost" size="sm">
          <Icon name="RefreshCw" size={18} />
        </Button>
      </div>
      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex-1 relative">
          <Icon name="Search" size={18} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search members..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e?.target?.value)}
          className="px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="paused">Paused</option>
          <option value="cancelled">Cancelled</option>
          <option value="expired">Expired</option>
        </select>
      </div>
      {/* Subscriptions List */}
      <div className="space-y-3 max-h-[600px] overflow-y-auto">
        {filteredSubscriptions?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="CreditCard" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4 opacity-50" />
            <p className="text-muted-foreground">No subscriptions found</p>
          </div>
        ) : (
          filteredSubscriptions?.map((subscription) => (
            <div
              key={subscription?.id}
              className="p-4 bg-background rounded-lg border border-border hover:border-primary transition-smooth"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3 flex-1">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                    <Icon name="User" size={20} color="var(--color-primary)" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-foreground truncate">
                      {subscription?.user_profiles?.full_name || 'Unknown'}
                    </p>
                    <p className="text-sm text-muted-foreground truncate">
                      {subscription?.user_profiles?.email}
                    </p>
                  </div>
                </div>
                <span className={`text-xs px-3 py-1 rounded-full font-medium ${getStatusBadgeColor(subscription?.status)}`}>
                  {subscription?.status}
                </span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground text-xs mb-1">Plan</p>
                  <p className="font-medium text-foreground">
                    {subscription?.membership_plans?.plan_name || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs mb-1">Price</p>
                  <p className="font-medium text-success">
                    ${parseFloat(subscription?.final_price || 0)?.toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs mb-1">Billing Cycle</p>
                  <p className="font-medium text-foreground">
                    {getBillingCycleLabel(subscription?.billing_cycle)}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs mb-1">Next Billing</p>
                  <p className="font-medium text-foreground">
                    {formatDate(subscription?.next_billing_date)}
                  </p>
                </div>
              </div>

              {subscription?.auto_renew && (
                <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                  <Icon name="RefreshCw" size={14} />
                  <span>Auto-renewal enabled</span>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SubscriptionStatusDashboard;